import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import { fetchShareholderDividends } from '../shareholderDividendsApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './ShareholderDividendsPage.css';

export default function ShareholderDividendsPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const numLocale = isAr ? 'ar-SA' : 'en-US';

  const t = useMemo(() => ({
    title: isAr ? 'الأرباح الموزعة' : 'Distributed dividends',
    subtitle: isAr ? 'متابعة توزيعات الأرباح التاريخية والمتوقعة حسب نسبة ملكيتك' : 'Historical and expected distributions based on your ownership',
    loadFailed: isAr ? 'فشل التحميل' : 'Failed to load',
    loading: isAr ? 'جارٍ تحميل بيانات الأرباح...' : 'Loading dividend data...',
    kpiTotal: isAr ? 'إجمالي الأرباح المستلمة' : 'Total dividends received',
    kpiLast: isAr ? 'آخر دفعة مستلمة' : 'Last payment received',
    kpiNext: isAr ? 'الدفعة القادمة المتوقعة' : 'Next estimated payment',
    kpiYield: isAr ? 'متوسط العائد' : 'Average yield',
    noteOwnership: isAr ? 'نسبة ملكيتك الحالية:' : 'Your current ownership:',
    colPeriod: isAr ? 'الفترة' : 'Period',
    colTotal: isAr ? 'إجمالي التوزيع' : 'Total distribution',
    colMyShare: isAr ? 'حصتك' : 'Your share',
    colYield: isAr ? 'العائد %' : 'Yield %',
    colStatus: isAr ? 'الحالة' : 'Status',
    colDeclaredAt: isAr ? 'تاريخ الإعلان' : 'Declared at',
    colPaidAt: isAr ? 'تاريخ الصرف' : 'Paid at',
    statusPaid: isAr ? 'مصروف' : 'Paid',
    statusDeclared: isAr ? 'معلن' : 'Declared',
    empty: isAr ? 'لا توجد بيانات أرباح متاحة حالياً.' : 'No dividend data available.',
    sar: isAr ? 'ر.س' : 'SAR',
  }), [isAr]);

  const money = (v) => `${Number(v || 0).toLocaleString(numLocale)} ${t.sar}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchShareholderDividends();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t.loadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t.loadFailed]);

  const rows = useMemo(() => data?.records ?? [], [data]);

  return (
    <div className="page shdiv-page">
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="chart" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t.title}
            </span>
            <span className="list-page-card__sub">{t.subtitle}</span>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading ? (
          <div className="list-page-card__loading">{t.loading}</div>
        ) : (
        <>
          <div className="shdiv-kpis">
            <div className="shdiv-kpi"><div className="shdiv-kpi__label">{t.kpiTotal}</div><div className="shdiv-kpi__value">{money(data?.summary?.totalReceived)}</div></div>
            <div className="shdiv-kpi"><div className="shdiv-kpi__label">{t.kpiLast}</div><div className="shdiv-kpi__value">{money(data?.summary?.lastReceived)}</div></div>
            <div className="shdiv-kpi"><div className="shdiv-kpi__label">{t.kpiNext}</div><div className="shdiv-kpi__value">{money(data?.summary?.nextEstimated)}</div></div>
            <div className="shdiv-kpi"><div className="shdiv-kpi__label">{t.kpiYield}</div><div className="shdiv-kpi__value">{Number(data?.summary?.averageYieldPct || 0).toLocaleString(numLocale)}%</div></div>
          </div>

          <div className="shdiv-note">
            <Icon name="money" size={13} color="#02c3a6" />
            {t.noteOwnership} <strong>{Number(data?.myOwnershipPct || 0).toLocaleString(numLocale)}%</strong>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t.colPeriod}</th>
                  <th>{t.colTotal}</th>
                  <th>{t.colMyShare}</th>
                  <th>{t.colYield}</th>
                  <th>{t.colStatus}</th>
                  <th>{t.colDeclaredAt}</th>
                  <th>{t.colPaidAt}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.period}-${r.declaredAt}`}>
                    <td>{r.period}</td>
                    <td>{money(r.totalPoolAmount)}</td>
                    <td>{money(r.myAmount)}</td>
                    <td>{Number(r.myYieldPct || 0).toLocaleString(numLocale)}%</td>
                    <td>
                      <span className={`shdiv-status ${r.status === 'paid' ? 'shdiv-status--paid' : 'shdiv-status--declared'}`}>
                        {r.status === 'paid' ? t.statusPaid : t.statusDeclared}
                      </span>
                    </td>
                    <td>{r.declaredAt || '—'}</td>
                    <td>{r.paidAt || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="shdiv-empty">{t.empty}</div>}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
